/**
 * Resale dialog lets sellers re-list a product on WhatsApp with a pre-filled
 * message and product images. Sharing is platform-specific:
 * - Native: cached JPEG files via Capacitor Share
 * - Web: Web Share API with File objects when supported
 */
import { useState, useMemo } from "react";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { Clipboard as CapacitorClipboard } from "@capacitor/clipboard";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Media } from "@capacitor-community/media";
import { Clipboard, Download, MessageCircle, X } from "lucide-react";
import { openExternalUrl } from "@/lib/capacitor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { philosopher } from "@/lib/fonts";
import { headerBg3Style } from "@/assets";
import { getAssetUrl } from "@/lib/getAssetUrl";
import { IProduct, IProductVariant, priceFormat } from "muminsmart-utils";
import { toast } from "sonner";

// --- Image helpers for sharing ---

/** Strip a data-URL prefix if CapacitorHttp/FileReader included one. */
function stripBase64Prefix(data: string): string {
  const commaIndex = data.indexOf(",");
  if (data.startsWith("data:") && commaIndex !== -1) {
    return data.slice(commaIndex + 1);
  }

  return data;
}

/** Strip the data-URL prefix so Filesystem.writeFile receives raw base64. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read image data"));
        return;
      }

      const base64 = stripBase64Prefix(result);
      if (!base64) {
        reject(new Error("Failed to read image data"));
        return;
      }

      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read image data"));
    reader.readAsDataURL(blob);
  });
}

/** WhatsApp rejects webp/heic more often than Instagram; JPEG is the safe share format. */
async function toJpegBase64(base64: string, mimeType: string): Promise<string> {
  const mime = (mimeType.split(";")[0] || "image/jpeg").trim().toLowerCase();

  if (mime === "image/jpeg" || mime === "image/jpg") {
    return base64;
  }

  const dataUrl = `data:${mime};base64,${base64}`;

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode image for sharing"));
    img.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Failed to convert image for sharing");
  }

  context.drawImage(image, 0, 0);
  const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const jpegBase64 = stripBase64Prefix(jpegDataUrl);

  if (!jpegBase64) {
    throw new Error("Failed to convert image for sharing");
  }

  return jpegBase64;
}

/** CapacitorHttp header keys differ by platform (Content-Type vs content-type). */
function getResponseContentType(
  headers: Record<string, string> | undefined,
): string | undefined {
  if (!headers) {
    return undefined;
  }

  return headers["Content-Type"] ?? headers["content-type"];
}

/**
 * Download a CDN image as base64 for native Filesystem writes.
 * On native we use CapacitorHttp instead of fetch because WebView fetch is
 * subject to CORS, even though the same URLs work in <img> tags.
 */
async function fetchImageBase64(
  imageKey: string,
): Promise<{ base64: string; mimeType: string }> {
  const url = getAssetUrl(imageKey);

  if (Capacitor.isNativePlatform()) {
    // responseType "blob" returns base64 on native (not a JS Blob).
    const response = await CapacitorHttp.get({
      url,
      responseType: "blob",
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error("Failed to fetch image for sharing");
    }

    if (typeof response.data !== "string" || !response.data) {
      throw new Error("Failed to read image data");
    }

    const mimeType =
      getResponseContentType(response.headers)?.split(";")[0] ?? "image/jpeg";

    return { base64: stripBase64Prefix(response.data), mimeType };
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch image for sharing");
  }

  const blob = await response.blob();

  return {
    base64: await blobToBase64(blob),
    mimeType: blob.type || "image/jpeg",
  };
}

/** Build File objects for the browser Web Share API (web-only path). */
async function fetchImageFiles(imageUrls: string[]): Promise<File[]> {
  return Promise.all(
    imageUrls.map(async (imageUrl, index) => {
      const response = await fetch(getAssetUrl(imageUrl));

      if (!response.ok) {
        throw new Error("Failed to fetch image for sharing");
      }

      const blob = await response.blob();
      const mimeType = blob.type || "image/jpeg";
      const jpegBlob =
        mimeType === "image/jpeg" || mimeType === "image/jpg"
          ? blob
          : await new Promise<Blob>((resolve, reject) => {
              const imageUrl = URL.createObjectURL(blob);
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const context = canvas.getContext("2d");
                URL.revokeObjectURL(imageUrl);

                if (!context) {
                  reject(new Error("Failed to convert image for sharing"));
                  return;
                }

                context.drawImage(img, 0, 0);
                canvas.toBlob(
                  (converted) => {
                    if (!converted) {
                      reject(new Error("Failed to convert image for sharing"));
                      return;
                    }
                    resolve(converted);
                  },
                  "image/jpeg",
                  0.92,
                );
              };
              img.onerror = () => {
                URL.revokeObjectURL(imageUrl);
                reject(new Error("Failed to decode image for sharing"));
              };
              img.src = imageUrl;
            });

      return new File([jpegBlob], `product-${index + 1}.jpg`, {
        type: "image/jpeg",
      });
    }),
  );
}

/**
 * Persist CDN images to the app cache and return shareable file:// URIs.
 * writeFile alone does not return a URI suitable for Share.share — getUri is
 * required so Android FileProvider can grant read access to WhatsApp etc.
 */
async function writeImagesToCache(imageUrls: string[]): Promise<string[]> {
  const fileUris: string[] = [];

  for (let index = 0; index < imageUrls.length; index++) {
    const imageKey = imageUrls[index];
    let hash = 2166136261;
    for (
      let characterIndex = 0;
      characterIndex < imageKey.length;
      characterIndex++
    ) {
      hash ^= imageKey.charCodeAt(characterIndex);
      hash = Math.imul(hash, 16777619);
    }
    const fileName = `resale-product-${hash >>> 0}.jpg`;

    try {
      await Filesystem.stat({
        path: fileName,
        directory: Directory.Cache,
      });
    } catch {
      const { base64, mimeType } = await fetchImageBase64(imageKey);
      const jpegBase64 = await toJpegBase64(base64, mimeType);

      await Filesystem.writeFile({
        path: fileName,
        data: jpegBase64,
        directory: Directory.Cache,
      });
    }

    const { uri } = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Cache,
    });

    fileUris.push(uri);
  }

  return fileUris;
}

async function getResaleAlbumIdentifier(): Promise<string> {
  const albumName = "Mumin's Mart";
  let { albums } = await Media.getAlbums();
  let album = albums.find((item) => item.name === albumName);

  if (!album) {
    await Media.createAlbum({ name: albumName });
    ({ albums } = await Media.getAlbums());
    album = albums.find((item) => item.name === albumName);
  }

  if (!album) {
    throw new Error("Failed to create image album");
  }

  return album.identifier;
}

/** Capacitor Share and Web Share API use different cancel error shapes. */
function isShareCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }

  if (error instanceof Error) {
    return /cancel/i.test(error.message);
  }

  return false;
}

/** Trigger browser downloads for each image (desktop/mobile web, non-native). */
async function downloadImagesInBrowser(imageUrls: string[]): Promise<void> {
  await Promise.all(
    imageUrls.map(async (imageUrl, index) => {
      const response = await fetch(getAssetUrl(imageUrl));

      if (!response.ok) {
        throw new Error("Failed to fetch image for download");
      }

      const blob = await response.blob();
      const extFromKey = imageUrl.split(".").pop()?.toLowerCase();
      const extFromBlob = blob.type.split("/")[1]?.toLowerCase();
      const extension = extFromKey || extFromBlob || "jpg";
      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `product-${index + 1}.${extension}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(objectUrl);
    }),
  );
}

/**
 * Desktop/mobile browsers can't reliably hand files straight to WhatsApp (no
 * Web Share Level 2 support, or WhatsApp isn't a registered share target), so
 * fall back to downloading the images and opening WhatsApp with the caption
 * pre-filled for the user to attach manually.
 */
async function shareOnWhatsAppWebFallback(
  shareMessage: string,
  imageUrls: string[],
): Promise<void> {
  if (imageUrls.length) {
    await downloadImagesInBrowser(imageUrls);
  }

  try {
    await CapacitorClipboard.write({ string: shareMessage });
  } catch {
    // Users can still type the caption manually in WhatsApp.
  }

  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  await openExternalUrl(waUrl);

  toast.success(
    imageUrls.length
      ? "Images downloaded and WhatsApp opened. Attach the downloaded images and paste the copied message."
      : "WhatsApp opened with your message. Paste it into the chat.",
  );
}

interface ResaleDialogProps {
  product: IProduct;
  selectedVariant: IProductVariant;
}

function ResaleDialog({ product, selectedVariant }: ResaleDialogProps) {
  const [sellingPrice, setSellingPrice] = useState<string>("");
  const [isSharingViaWhatsApp, setIsSharingViaWhatsApp] = useState(false);
  const [isCopyingMessage, setIsCopyingMessage] = useState(false);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);

  /**
   * Collect image keys to attach when sharing.
   * Android: up to 10 images, split across in-stock variants (WhatsApp limit).
   * iOS/other: featured + first additional image per in-stock variant.
   */
  const productImagesToShare = useMemo(() => {
    if (!product) {
      return [];
    }

    const isAndroid = Capacitor.getPlatform() === "android";

    const imageKeys: string[] = [];

    const availableVariants = product.variants.filter(
      (variant) => variant.alwaysAvailable || variant.stock > 0,
    );

    if (isAndroid) {
      const maxImages = 10;
      const perVariantImageCount = Math.floor(
        maxImages / availableVariants.length,
      );

      let imageCount = 0;
      for (const variant of availableVariants) {
        if (imageCount >= maxImages) {
          break;
        }
        const productImages = [
          variant.featuredImage,
          ...variant.additionalImages,
        ];
        for (let i = 0; i < perVariantImageCount; i++) {
          if (imageCount >= maxImages) {
            break;
          }
          const imageKey = productImages[i];
          if (imageKey) {
            imageKeys.push(imageKey);
            imageCount++;
          }
        }
      }
    } else {
      // Lighter set for iOS — UIActivityViewController handles fewer attachments well.
      for (const variant of availableVariants) {
        if (variant.featuredImage) {
          imageKeys.push(variant.featuredImage);
        }

        const firstAdditionalImage = variant.additionalImages[0];
        if (firstAdditionalImage) {
          imageKeys.push(firstAdditionalImage);
        }
      }
    }

    return imageKeys;
  }, [product]);

  const sharePrice = sellingPrice.trim()
    ? sellingPrice + "/-"
    : selectedVariant.discountedPrice + "/-";
  const shareMessage = `${product.name}\n\n${product.description}\n\nPrice: ${sharePrice}`;

  const handleCopyMessage = async () => {
    if (isCopyingMessage) return;

    try {
      setIsCopyingMessage(true);
      await CapacitorClipboard.write({ string: shareMessage });
      toast.success("Message copied to clipboard.");
    } catch {
      toast.error("Unable to copy message right now. Please try again.");
    } finally {
      setIsCopyingMessage(false);
    }
  };

  /**
   * Share product images + message via the system share sheet (WhatsApp, etc.).
   * Does not fall back to text-only when images exist — that would look like a
   * successful image share while only sending the caption.
   */
  const handleShareOnWhatsApp = async () => {
    if (isSharingViaWhatsApp) return;
    setIsSharingViaWhatsApp(true);

    try {
      const normalizedImageUrls = Array.from(
        new Set(
          (productImagesToShare ?? [])
            .map((item) => item.trim())
            .filter(Boolean),
        ),
      );

      if (Capacitor.isNativePlatform()) {
        const { value: canShare } = await Share.canShare();

        if (!canShare) {
          toast.error(
            "Sharing is not supported on this device. You can download the images, copy the message, and share them manually.",
          );
          return;
        }

        if (normalizedImageUrls.length) {
          const files = await writeImagesToCache(normalizedImageUrls);

          try {
            await CapacitorClipboard.write({ string: shareMessage });
          } catch {
            // Users can still manually add the caption after sharing images.
          }

          toast.success("Message copied. Paste it as the caption in WhatsApp.");
          await Share.share({
            files,
            dialogTitle: "Share on WhatsApp",
          });
          return;
        }

        // No images available — share the text message only.
        await Share.share({
          title: product.name,
          text: shareMessage,
          dialogTitle: "Share on WhatsApp",
        });
        return;
      }

      // Web browser (mobile or desktop): try the Web Share API with files first —
      // this covers mobile Chrome/Safari, which can hand files straight to WhatsApp.
      if (
        normalizedImageUrls.length &&
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        typeof navigator.canShare === "function"
      ) {
        try {
          const files = await fetchImageFiles(normalizedImageUrls);

          if (files.length && navigator.canShare({ files })) {
            try {
              await CapacitorClipboard.write({ string: shareMessage });
            } catch {
              // Users can still manually add the caption after sharing images.
            }

            await navigator.share({ files, text: shareMessage });
            return;
          }
        } catch (error) {
          if (isShareCancelled(error)) {
            toast.error("Sharing was cancelled.");
            return;
          }
          // Web Share failed (e.g. desktop browser, or WhatsApp isn't a
          // registered share target) — fall through to the manual fallback.
        }
      }

      // Desktop browsers, or browsers without Web Share file support: download
      // the images and open WhatsApp with the message pre-filled instead.
      await shareOnWhatsAppWebFallback(shareMessage, normalizedImageUrls);
    } catch (error) {
      if (isShareCancelled(error)) {
        toast.error("Sharing was cancelled.");
      } else {
        toast.error("Unable to share right now. Please try again.");
      }
    } finally {
      setIsSharingViaWhatsApp(false);
    }
  };

  const handleDownloadImages = async () => {
    if (isDownloadingImages) return;

    setIsDownloadingImages(true);

    try {
      const normalizedImageUrls = Array.from(
        new Set(
          (productImagesToShare ?? [])
            .map((item) => item.trim())
            .filter(Boolean),
        ),
      );

      if (!normalizedImageUrls.length) {
        toast.error("No images available to download.");
        return;
      }

      if (Capacitor.getPlatform() === "android") {
        const files = await writeImagesToCache(normalizedImageUrls);
        const albumIdentifier = await getResaleAlbumIdentifier();
        const downloadTimestamp = Date.now();

        await Promise.all(
          files.map((path, index) =>
            Media.savePhoto({
              path,
              albumIdentifier,
              fileName: `product-${downloadTimestamp}-${index + 1}`,
            }),
          ),
        );

        toast.success("Images saved to your gallery.");
        return;
      }

      await downloadImagesInBrowser(normalizedImageUrls);

      toast.success("Images download started.");
    } catch {
      toast.error("Unable to download images right now. Please try again.");
    } finally {
      setIsDownloadingImages(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#d7d0c7] py-3 text-base text-[#2f2a27]"
        >
          Re-Sale
        </button>
      </DialogTrigger>
      <DialogContent
        className="rounded-4xl bg-repeat-x"
        style={headerBg3Style}
        showCloseButton={false}
      >
        <DialogHeader className="flex flex-row items-center justify-end">
          <DialogClose
            className="flex items-center justify-center rounded-full bg-secondary text-white shadow-sm"
            asChild
          >
            <X className="p-1 size-8" />
          </DialogClose>
        </DialogHeader>
        <form>
          <div className="space-y-3">
            <h1
              className={`text-3xl font-bold text-[#2F2F2F] ${philosopher.className}`}
            >
              {product.name}
            </h1>

            <p
              className={`text-3xl font-bold text-[#2F2F2F] ${philosopher.className}`}
            >
              {priceFormat(selectedVariant.discountedPrice)}
            </p>

            <div>
              <label className="sr-only" htmlFor="resale-price-input">
                Enter selling price
              </label>
              <Input
                id="resale-price-input"
                type="number"
                min={1}
                value={sellingPrice}
                onChange={(event) => setSellingPrice(event.target.value)}
                placeholder="ENTER SELLING PRICE"
                className="h-14 rounded-full border-[#d8d2cb] bg-[#f2efed] px-6 text-base text-[#5a544f] placeholder:text-sm placeholder:font-semibold placeholder:tracking-[0.08em] placeholder:text-[#77706b]"
              />
            </div>

            <div className="space-y-3">
              <Button
                type="button"
                disabled={isCopyingMessage}
                variant="outline"
                className="h-14 w-full rounded-full border-[#d8d2cb] bg-white text-lg font-medium text-[#104d5a] hover:bg-[#f2efed] disabled:opacity-60"
                onClick={() => {
                  void handleCopyMessage();
                }}
              >
                <Clipboard className="h-5 w-5" />
                {isCopyingMessage ? "Copying..." : "Copy Message"}
              </Button>
              <Button
                type="button"
                disabled={isSharingViaWhatsApp}
                className="h-14 w-full rounded-full bg-[#104d5a] text-lg font-medium text-white hover:bg-[#0c404b] disabled:opacity-60"
                onClick={() => {
                  void handleShareOnWhatsApp();
                }}
              >
                <MessageCircle className="h-5 w-5 text-[#25D366]" />
                {isSharingViaWhatsApp
                  ? "Opening..."
                  : "Share Images On Whatsapp"}
              </Button>
              <Button
                type="button"
                disabled={isDownloadingImages}
                variant="outline"
                className="h-14 w-full rounded-full border-[#d8d2cb] bg-white text-lg font-medium text-[#104d5a] hover:bg-[#f2efed] disabled:opacity-60"
                onClick={() => {
                  void handleDownloadImages();
                }}
              >
                <Download className="h-5 w-5" />
                {isDownloadingImages ? "Preparing..." : "Download Images"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ResaleDialog;
